import { useEffect, useRef, useState } from 'react'
import { useStore } from '@xyflow/react'
import type { Rect } from './pictureOps'

/**
 * Crop mode on a picture (Huanrui, 2026-10-06): a double-click puts a box on
 * it with eight handles. The box itself can be dragged to move it.
 *
 * SMALLER CROPS, LARGER EXPANDS (Huanrui, 2026-10-26). Inside the picture the
 * box crops -- free, and done at once. Dragged out past an edge it expands:
 * Bria Expand (fal) draws what is new, and the picture itself is laid back
 * over it untouched (ProjectCanvas, expandPicture). A side may go out by as
 * much again as the picture. One box, one Ratio, both ways; a box can crop
 * one side and expand another. While it expands the new area is hatched,
 * the bar takes an optional line of what goes there, and the button says
 * Expand and what it costs. (A general model was tried for this on
 * 2026-10-06 and taken out: it redrew the whole picture.)
 *
 * An expand costs money, so a click away from an expanding box leaves it
 * (as Escape does); a crop is free and a click away still finishes it.
 *
 * KEEPING THE SHAPE (Huanrui, 2026-10-07). A Ratio button left of Crop locks
 * the box to a shape -- 1:1, 4:3, 3:4, 16:9 or 9:16, no more (Huanrui) -- and puts
 * the largest box of that shape round the current one's centre. Holding
 * Shift while dragging any handle keeps the shape the box had when the drag
 * began, corners and edges alike; an edge grows the other way about the
 * middle. Either way the box shrinks to stay inside the picture rather than
 * losing its shape at an edge.
 *
 * Enter, a double-click inside the box, the Crop button or a click anywhere
 * else finishes. Escape or Cancel leaves it.
 *
 * Everything here is in the node's own units (the board's, before zooming);
 * lines, handles and the button bar are drawn at a steady size on screen
 * whatever the zoom. The wheel still zooms the board over a picture being
 * cropped (Huanrui, 2026-10-06), so a small picture can be brought close.
 */

type Grip = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | 'move'

/** The shapes on offer. `null` is free; 'original' is the picture's own. */
type Ratio = null | 'original' | [number, number]
const RATIOS: { label: string; ratio: Ratio }[] = [
  { label: 'Free', ratio: null },
  { label: '1:1', ratio: [1, 1] },
  { label: '4:3', ratio: [4, 3] },
  { label: '3:4', ratio: [3, 4] },
  { label: '16:9', ratio: [16, 9] },
  { label: '9:16', ratio: [9, 16] },
]

const GRIPS: Grip[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

const zoomOf = (state: { transform: [number, number, number] }) => state.transform[2]

export function CropBox({
  width,
  height,
  credits,
  onDone,
  onExpand,
  onCancel,
}: {
  width: number
  height: number
  /** About what one expand costs, for the button. */
  credits?: number
  onDone: (rect: Rect) => void
  /** The box past the picture's edges, and what goes round it. Without it, the box stays inside. */
  onExpand?: (rect: Rect, prompt: string) => void
  onCancel: () => void
}) {
  const zoom = useStore(zoomOf) || 1
  const [rect, setRect] = useState<Rect>({ x: 0, y: 0, w: width, h: height })
  const [chosen, setChosen] = useState(0)
  const [menu, setMenu] = useState(false)
  const [prompt, setPrompt] = useState('')
  const preset = RATIOS[chosen]!.ratio
  /** Width over height the box is held to, or 0 when it is free. */
  const locked = preset === null ? 0 : preset === 'original' ? width / height : preset[0] / preset[1]
  const root = useRef<HTMLDivElement | null>(null)
  const held = useRef<{ grip: Grip; sx: number; sy: number; from: Rect } | null>(null)

  const unchanged =
    Math.abs(rect.x) < 0.5 && Math.abs(rect.y) < 0.5 && Math.abs(rect.w - width) < 0.5 && Math.abs(rect.h - height) < 0.5
  /** Past an edge anywhere: an expand. */
  const outside = rect.x < -0.5 || rect.y < -0.5 || rect.x + rect.w > width + 0.5 || rect.y + rect.h > height + 0.5

  const latest = useRef({ rect, unchanged, outside, prompt, onDone, onExpand, onCancel })
  latest.current = { rect, unchanged, outside, prompt, onDone, onExpand, onCancel }

  const finish = () => {
    const now = latest.current
    if (now.unchanged) now.onCancel()
    else if (now.outside) now.onExpand?.(now.rect, now.prompt.trim())
    else now.onDone(now.rect)
  }

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        latest.current.onCancel()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        finish()
      } else if (['Delete', 'Backspace'].includes(event.key) && !(event.target instanceof HTMLInputElement)) {
        // Not the picture: it is being cropped, not deleted.
        event.stopPropagation()
      }
    }
    const away = (event: PointerEvent) => {
      if (root.current?.contains(event.target as globalThis.Node)) return
      // A crop is free and finishes; an expand costs money and is left.
      if (latest.current.outside) latest.current.onCancel()
      else finish()
    }
    window.addEventListener('keydown', key, true)
    window.addEventListener('pointerdown', away, true)
    return () => {
      window.removeEventListener('keydown', key, true)
      window.removeEventListener('pointerdown', away, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const MIN = 16
  // Inside the picture, or -- where it can expand -- out by as much again as the picture each way.
  const lim = onExpand ? { x0: -width, y0: -height, x1: 2 * width, y1: 2 * height } : { x0: 0, y0: 0, x1: width, y1: height }

  const drag = (event: React.PointerEvent, grip: Grip) => {
    if (event.button !== 0) return
    event.stopPropagation()
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    held.current = { grip, sx: event.clientX, sy: event.clientY, from: rect }
  }
  /**
   * The largest box of this shape that fits the picture, round the centre of
   * the box there is now, moved back inside if it would hang over an edge.
   */
  const pick = (index: number) => {
    setChosen(index)
    setMenu(false)
    const r = RATIOS[index]!.ratio
    if (r === null) return
    const aspect = r === 'original' ? width / height : r[0] / r[1]
    const w = Math.min(width, height * aspect)
    const h = w / aspect
    const cx = rect.x + rect.w / 2
    const cy = rect.y + rect.h / 2
    setRect({
      x: Math.min(Math.max(cx - w / 2, 0), width - w),
      y: Math.min(Math.max(cy - h / 2, 0), height - h),
      w,
      h,
    })
  }

  /** A handle dragged with the shape held: `aspect` is width over height. */
  const shaped = (grip: Grip, f: Rect, dx: number, dy: number, aspect: number): Rect => {
    const right = f.x + f.w
    const bottom = f.y + f.h
    if (grip.length === 2) {
      // A corner: the opposite corner stays put, led by whichever way moved more.
      const ax = grip.includes('w') ? right : f.x
      const ay = grip.includes('n') ? bottom : f.y
      let w = Math.max(MIN, grip.includes('w') ? f.w - dx : f.w + dx)
      let h = Math.max(MIN, grip.includes('n') ? f.h - dy : f.h + dy)
      if (w / f.w > h / f.h) h = w / aspect
      else w = h * aspect
      const roomW = grip.includes('w') ? ax - lim.x0 : lim.x1 - ax
      const roomH = grip.includes('n') ? ay - lim.y0 : lim.y1 - ay
      const fit = Math.min(1, roomW / w, roomH / h)
      w *= fit
      h *= fit
      return { x: grip.includes('w') ? ax - w : ax, y: grip.includes('n') ? ay - h : ay, w, h }
    }
    if (grip === 'e' || grip === 'w') {
      // A side: the other side stays put, and the height grows about the middle.
      const cy = f.y + f.h / 2
      let w = Math.max(MIN, grip === 'w' ? f.w - dx : f.w + dx)
      let h = w / aspect
      const roomW = grip === 'w' ? right - lim.x0 : lim.x1 - f.x
      const roomH = 2 * Math.min(cy - lim.y0, lim.y1 - cy)
      const fit = Math.min(1, roomW / w, roomH / h)
      w *= fit
      h *= fit
      return { x: grip === 'w' ? right - w : f.x, y: cy - h / 2, w, h }
    }
    // Top or bottom: the width grows about the middle.
    const cx = f.x + f.w / 2
    let h = Math.max(MIN, grip === 'n' ? f.h - dy : f.h + dy)
    let w = h * aspect
    const roomH = grip === 'n' ? bottom - lim.y0 : lim.y1 - f.y
    const roomW = 2 * Math.min(cx - lim.x0, lim.x1 - cx)
    const fit = Math.min(1, roomW / w, roomH / h)
    w *= fit
    h *= fit
    return { x: cx - w / 2, y: grip === 'n' ? bottom - h : f.y, w, h }
  }

  const move = (event: React.PointerEvent) => {
    const h = held.current
    if (!h) return
    const dx = (event.clientX - h.sx) / zoom
    const dy = (event.clientY - h.sy) / zoom
    const f = h.from
    if (h.grip === 'move') {
      // Within the limits, and never off the picture altogether.
      const x = Math.min(Math.max(f.x + dx, lim.x0, -f.w + MIN), lim.x1 - f.w, width - MIN)
      const y = Math.min(Math.max(f.y + dy, lim.y0, -f.h + MIN), lim.y1 - f.h, height - MIN)
      setRect({ ...f, x, y })
      return
    }
    // A chosen ratio holds always; Shift holds the shape the drag began with.
    const aspect = locked || (event.shiftKey ? f.w / f.h : 0)
    if (aspect) {
      setRect(shaped(h.grip, f, dx, dy, aspect))
      return
    }
    let x0 = f.x
    let y0 = f.y
    let x1 = f.x + f.w
    let y1 = f.y + f.h
    // Never off the picture altogether: some of it stays in the box.
    if (h.grip.includes('w')) x0 = Math.min(Math.max(f.x + dx, lim.x0), x1 - MIN, width - MIN)
    if (h.grip.includes('e')) x1 = Math.max(Math.min(f.x + f.w + dx, lim.x1), x0 + MIN, MIN)
    if (h.grip.includes('n')) y0 = Math.min(Math.max(f.y + dy, lim.y0), y1 - MIN, height - MIN)
    if (h.grip.includes('s')) y1 = Math.max(Math.min(f.y + f.h + dy, lim.y1), y0 + MIN, MIN)
    setRect({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
  }
  const release = (event: React.PointerEvent) => {
    held.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  // The area drawn over: the picture and the box together.
  const ux0 = Math.min(0, rect.x)
  const uy0 = Math.min(0, rect.y)
  const ux1 = Math.max(width, rect.x + rect.w)
  const uy1 = Math.max(height, rect.y + rect.h)
  const box = (r: Rect) => `M${r.x} ${r.y}h${r.w}v${r.h}h${-r.w}Z`
  const picture = { x: 0, y: 0, w: width, h: height }
  const line = 1.5 / zoom
  const grip = 10 / zoom
  // Ids for the pattern and the clip, one set per box on the page.
  const hatch = useRef(Math.random().toString(36).slice(2, 8)).current

  return (
    <div
      ref={root}
      className="ccrop nodrag nopan"
      onPointerMove={move}
      onPointerUp={release}
      // A Shift-click here is a crop, not the board's add-to-selection.
      onClick={(event) => event.stopPropagation()}
    >
      <svg
        className="ccrop__shade"
        style={{ left: ux0, top: uy0, width: ux1 - ux0, height: uy1 - uy0 }}
        viewBox={`${ux0} ${uy0} ${ux1 - ux0} ${uy1 - uy0}`}
        aria-hidden="true"
      >
        <defs>
          <pattern id={`ccrop-new-${hatch}`} width={12 / zoom} height={12 / zoom} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={12 / zoom} height={12 / zoom} fill="rgba(120,160,255,0.16)" />
            <rect width={6 / zoom} height={12 / zoom} fill="rgba(120,160,255,0.32)" />
          </pattern>
          <clipPath id={`ccrop-in-${hatch}`}>
            <path d={box(picture)} />
          </clipPath>
        </defs>
        {/* The picture outside the box: what a crop leaves out. */}
        <path d={box(picture) + box(rect)} fillRule="evenodd" fill="rgba(0,0,0,0.6)" clipPath={`url(#ccrop-in-${hatch})`} />
        {/* The box outside the picture: what an expand draws. */}
        {outside ? <path d={box(rect) + box(picture)} fillRule="evenodd" fill={`url(#ccrop-new-${hatch})`} /> : null}
      </svg>

      <div
        className="ccrop__box"
        style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, borderWidth: line }}
        onPointerDown={(event) => drag(event, 'move')}
        onDoubleClick={(event) => {
          event.stopPropagation()
          finish()
        }}
      >
        {GRIPS.map((one) => (
          <span
            key={one}
            className={`ccrop__grip ccrop__grip--${one}`}
            style={{ width: grip, height: grip, borderWidth: line }}
            onPointerDown={(event) => drag(event, one)}
          />
        ))}
      </div>

      <div
        className="ccrop__bar"
        style={{ left: rect.x, top: rect.y + rect.h, transform: `translateY(${10 / zoom}px) scale(${1 / zoom})` }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="ccrop__btn" onClick={() => latest.current.onCancel()}>
          Cancel
        </button>
        <span className="ccrop__ratio">
          <button
            type="button"
            className={`ccrop__btn${chosen > 0 ? ' ccrop__btn--set' : ''}`}
            onClick={() => setMenu((open) => !open)}
            title="Keep the box to a shape. Shift while dragging keeps its current shape."
          >
            {chosen > 0 ? RATIOS[chosen]!.label : 'Ratio'} ▾
          </button>
          {menu ? (
            <span className="ccrop__menu" role="menu">
              {RATIOS.map((one, index) => (
                <button
                  key={one.label}
                  type="button"
                  role="menuitemradio"
                  aria-checked={index === chosen}
                  className={`ccrop__item${index === chosen ? ' ccrop__item--on' : ''}`}
                  onClick={() => pick(index)}
                >
                  {one.label}
                </button>
              ))}
            </span>
          ) : null}
        </span>
        {outside ? (
          <input
            className="ccrop__prompt"
            type="text"
            value={prompt}
            maxLength={300}
            placeholder="What goes round it (optional, English)"
            onChange={(event) => setPrompt(event.target.value)}
          />
        ) : null}
        <button
          type="button"
          className="ccrop__btn ccrop__btn--go"
          disabled={unchanged}
          onClick={finish}
          title={outside ? 'Only what is new is drawn; the picture itself is kept exactly as it is' : undefined}
        >
          {outside ? (
            <>
              Expand <span className="ccrop__credits">· {credits ?? 40} credits</span>
            </>
          ) : (
            'Crop'
          )}
        </button>
      </div>
    </div>
  )
}
